// login-controller.ts (inside your stencil package)

import { CARD_SWITCH_FAILED_MESSAGE, PathMap, SMARTBUY_PATH } from '../config';
import { CommonApiService } from './common-api-service';

interface RequestLoginOptions {
  eventName: string;
  notifyParent: boolean;
  isLoginRequired: boolean;
}

interface showLoaderOptions {
  title: string;
  cardFaciaUrl?: string;
  subTitle?: string;
  status?: 'loading' | 'failed';
}

export interface CheckLoginOptions {
  eventName?: string;
  notifyParent?: boolean;
  showLogin?: boolean;
  showAddCard?: boolean;
  addCardTitle?: string;
  responseBack?: boolean;
  replace?: boolean;
  redirectBackOnCancel?: boolean;
  redirectPath?: string;
  optCardBasedOnPath?: { path: string; opt: boolean };
  addCardMandatory?: boolean;
  isLoginRequired?: boolean;
  forceLogin?: boolean;
}

interface AddCardOptions {
  eventName?: 'show-add-card-with-session' | 'show-add-card-without-session';
  notifyParent?: boolean;
  addCardTitle?: string;
}

export interface loginResponse {
  authenticated?: boolean;
  cancelled?: boolean;
  info?: any;
}

export interface addCardResponse {
  info?: any;
  cancelled?: boolean;
  authenticated?: boolean;
}
type LoginCallback = (loginInfo: loginResponse) => void;
type AddCardCallback = (loginInfo: addCardResponse) => void;

class LoginController {
  private static instance: LoginController;
  private CommonApiService = new CommonApiService();

  static getInstance() {
    if (!LoginController.instance) {
      LoginController.instance = new LoginController();
    }
    return LoginController.instance;
  }

  showLoader(
    options: showLoaderOptions = {
      cardFaciaUrl: 'default.webp',
      title: 'Loading...',
      subTitle: '',
      status: 'loading'
    }
  ) {
    const event = new CustomEvent('show-loader', {
      detail: options
    });
    window.dispatchEvent(event);
  }

  hideLoader() {
    const event = new CustomEvent('hide-loader');
    window.dispatchEvent(event);
  }

  requestLogin(callback: LoginCallback, options: RequestLoginOptions = { eventName: 'show-smartbuy-login', notifyParent: false, isLoginRequired: false }) {
    const event = new CustomEvent(options.eventName, {
      detail: {
        callback,
        ...options
      }
    });
    window.dispatchEvent(event);
  }

  requestAddCard(callback: AddCardCallback, options?: AddCardOptions) {
    const defaultOptions: AddCardOptions = { eventName: 'show-add-card-with-session', notifyParent: false };
    options = { ...defaultOptions, ...options };

    const event = new CustomEvent(options.eventName, {
      detail: {
        callback,
        ...options
      }
    });
    window.dispatchEvent(event);
  }

  private showAddCard = (options: any, loginInfo: any, callback: Function, addCardViaOtpResp: boolean) => {
    this.requestAddCard(
      res => {
        const { info: cardInfo } = res || {};
        if (cardInfo.authenticated) {
          if (options.responseBack) {
            callback({ ...loginInfo, ...cardInfo, cardAdded: true });
          } else {
            const path = PathMap[cardInfo.cardType as keyof typeof PathMap] || '';
            if (options.replace) {
              options.redirectPath = options.redirectPath.replace(/:cardType/, path);
              window.location.replace(options.redirectPath);
            } else {
              options.redirectPath = options.redirectPath.replace(/:cardType/, path);
              window.location.href = options.redirectPath || '/';
            }
          }
        } else {
          if (options.addCardMandatory) {
            if (options.redirectBackOnCancel) {
              if (document.referrer && document.referrer.startsWith(window.location.origin)) {
                window.history.back();
              } else {
                window.location.href = '/';
              }
            } else {
              callback({ ...loginInfo, cardAdded: false });
            }
          } else {
            if (options.responseBack) {
              callback({ ...loginInfo, cardAdded: false });
            } else {
              window.location.reload();
            }
          }
        }
      },
      { eventName: 'show-add-card-with-session', ...options, loginInfo: { ...loginInfo, viaOtpResponse: addCardViaOtpResp } }
    );
  };

  private selectCard = async (res: any, card: any) => {
    try {
      const resp = await this.CommonApiService.post('/api/getcustomercardpoints', {
        ctype: 'flights',
        _token: res['XSRF-TOKEN'],
        takecard: card.id
      });
      if (resp.status === true || resp.status === "true") {
        return { ...resp, status: true };
      } else {
        return { error: 'Failed card select', message: resp.message, status: false };
      }
    } catch (error) {
      return { error: 'Failed to select card', status: false };
    }
  };

  private selectCardFromPath = async (options: any, res: any, callback: Function, addCardViaOtpResp: boolean) => {
    const path = options.optCardBasedOnPath.path;
    const card = res?.customerCards?.find((card: any) => PathMap[card.cardType] === path);
    if (card?.cardType || path === SMARTBUY_PATH) {
      let cardType = '';
      let selectedCard = card;

      if (path === SMARTBUY_PATH) {
        selectedCard = res?.customerCards?.[0];
        cardType = selectedCard?.cardType || '';
      } else {
        cardType = card.cardType;
      }
      if (cardType) {
        cardType = cardType[0].toUpperCase() + cardType?.slice(1)?.toLowerCase();
        let cardName = cardType.replaceAll('_', ' ');
        if (path === SMARTBUY_PATH) cardName = SMARTBUY_PATH;
        this.showLoader({
          title: 'Taking you to ' + cardName + ' experience',
          cardFaciaUrl: card?.imageUrl || ''
        });
      }
      const resp = await this.selectCard(res, selectedCard);
      if (resp.status) {
        if (options.responseBack) {
          callback({ ...resp, ...res, cardAdded: true });
        } else {
          const path = PathMap[resp.client_type as keyof typeof PathMap] || '';
          if (options.replace) {
            options.redirectPath = options.redirectPath.replace(/:cardType/, path);
            window.location.replace(options.redirectPath);
          } else {
            options.redirectPath = options.redirectPath.replace(/:cardType/, path);
            if (!options.redirectPath.startsWith('/')) {
              options.redirectPath = '/' + options.redirectPath;
            }
            window.location.href = options.redirectPath || '/';
          }
        }
      } else {
        this.showLoader({
          title: resp.message || CARD_SWITCH_FAILED_MESSAGE,
          cardFaciaUrl: '',
          subTitle: 'Taking you back!',
          status: 'failed'
        });
        setTimeout(() => {
          if (document.referrer && document.referrer.startsWith(window.location.origin)) {
            window.history.back();
          } else {
            window.location.href = '/';
          }
        }, 3000);
      }
    } else {
      this.showAddCard(options, res, callback, addCardViaOtpResp);
    }
  };

  loginCallBack(res: any, options: any, callback: Function) {
    const { info } = res || {};
    if (info.authenticated) {
      if ((!info?.customerCards || info?.customerCards?.length === 0) && options.showAddCard) {
        this.showAddCard({ ...options, eventName: 'show-add-card-without-session' }, info, callback, true);
      } else if (options.optCardBasedOnPath?.opt) {
        this.selectCardFromPath({ ...options, eventName: 'show-add-card-without-session' }, info, callback, true);
      } else if (options.responseBack) {
        callback(info);
      } else {
        this.showLoader({
          title: 'Authenticating...'
        });
        const card = info?.customerCards?.[0]?.cardType || '';
        const path = PathMap[card as keyof typeof PathMap] || '';
        if (card) {
          if (options.replace) {
            options.redirectPath = options.redirectPath.replace(/:cardType/, path);
            window.location.replace(options.redirectPath);
          } else {
            options.redirectPath = options.redirectPath.replace(/:cardType/, path);
            window.location.href = options.redirectPath || '/';
          }
        } else {
          window.location.reload();
        }
      }
    } else {
      if (options.redirectBackOnCancel) {
        if (document.referrer && document.referrer.startsWith(window.location.origin)) {
          window.history.back();
        } else {
          window.location.href = '/';
        }
      } else {
        callback(info);
      }
    }
  }

  checkLogin(callback: LoginCallback, opts: CheckLoginOptions) {
    const defaultOptions: CheckLoginOptions = { eventName: 'check-login', notifyParent: false };
    const options = { ...defaultOptions, ...opts, isAddCardRequired: opts.addCardMandatory };

    if (options.forceLogin) {
      this.requestLogin(loginInfo => this.loginCallBack(loginInfo, options, callback), {
        eventName: 'show-smartbuy-login',
        notifyParent: false,
        isLoginRequired: options.isLoginRequired
      });
    }
    const internalCallback = async (res: any) => {
      if (res.isLoggedin) {
        if (res?.customerCards?.length === 0 && options.showAddCard && options.addCardMandatory) {
          this.showAddCard({ ...options, eventName: 'show-add-card-with-session' }, res, callback, false);
        } else if (options.optCardBasedOnPath?.opt) {
          if (PathMap[res?.ccustomer?.cardType] === options.optCardBasedOnPath.path) {
            callback(res);
            return;
          }
          this.selectCardFromPath({ ...options, eventName: 'show-add-card-with-session' }, res, callback, false);
        } else {
          callback(res);
        }
      } else {
        if (options.showLogin) {
          this.requestLogin(loginInfo => this.loginCallBack(loginInfo, options, callback), {
            eventName: 'show-smartbuy-login',
            notifyParent: false,
            isLoginRequired: options.isLoginRequired
          });
        } else {
          callback(res);
        }
      }
    };
    const event = new CustomEvent(options.eventName, {
      detail: {
        callback,
        internalCallback,
        ...options
      }
    });
    window.dispatchEvent(event);
  }
}

export const loginController = LoginController.getInstance();
